// Bridge between the proposal reader (inside the sandboxed iframe) and the API.
// The iframe reports deltas every ~2 s; this batches them and ships every 10 s,
// with a beacon when the page goes away. Each flush doubles as the "live" heartbeat.
import { watchCaptures } from './capture';
import type { Plan, SelectionItem } from './types';

export interface SectionInfo {
  key: string;
  label: string;
  pos: number;
}

interface Pending {
  activeMs: number;
  maxScroll: number;
  section: string | null;
  visible: boolean;
  sections: Map<string, SectionInfo & { ms: number }>;
  events: Array<{ kind: string; section: string | null; data: Record<string, unknown> }>;
}

export interface Snapshot {
  items: SelectionItem[];
  plan: Plan | null;
  section: string | null;
}

const FLUSH_MS = 10_000;
const MAX_EVENTS = 80;

export interface Reading {
  snapshot: () => Promise<Snapshot>;
  currentSection: () => string | null;
  stop: () => void;
}

export const startReading = (opts: {
  viewId: string | null;
  frame: HTMLIFrameElement;
  onSections: (list: SectionInfo[]) => void;
}): Reading => {
  const { viewId, frame } = opts;
  const p: Pending = { activeMs: 0, maxScroll: 0, section: null, visible: true, sections: new Map(), events: [] };
  const waiting = new Map<string, (v: Snapshot) => void>();
  let current: string | null = null;

  const payload = () => ({
    activeMs: Math.round(p.activeMs),
    maxScroll: Math.round(p.maxScroll),
    section: p.section,
    visible: document.visibilityState === 'visible',
    sections: [...p.sections.values()].map((s) => ({ key: s.key, label: s.label, pos: s.pos, ms: Math.round(s.ms) })),
    events: p.events.slice(0, MAX_EVENTS),
  });
  const reset = () => {
    p.activeMs = 0;
    p.sections.clear();
    p.events = [];
  };

  const flush = (beacon = false) => {
    if (!viewId) return;
    const body = JSON.stringify(payload());
    reset();
    const url = `/api/views/${viewId}/events`;
    if (beacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
      return;
    }
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, credentials: 'same-origin', keepalive: true }).catch(() => undefined);
  };

  const onMessage = (e: MessageEvent) => {
    if (e.source !== frame.contentWindow || !e.data || e.data.metron !== 1) return;
    const m = e.data;
    if (m.type === 'ready') opts.onSections(Array.isArray(m.sections) ? m.sections : []);
    else if (m.type === 'progress') {
      p.activeMs += Number(m.activeMs) || 0;
      p.maxScroll = Math.max(p.maxScroll, Number(m.maxScroll) || 0);
      if (m.section) p.section = current = String(m.section).slice(0, 120);
      for (const s of Array.isArray(m.sections) ? m.sections : []) {
        const prev = p.sections.get(s.key);
        p.sections.set(s.key, { key: String(s.key).slice(0, 80), label: String(s.label).slice(0, 120), pos: Number(s.pos) || 0, ms: (prev?.ms ?? 0) + (Number(s.ms) || 0) });
      }
    } else if (m.type === 'event' && p.events.length < MAX_EVENTS) {
      if (['scroll', 'section', 'capture', 'click'].includes(m.kind)) p.events.push({ kind: m.kind, section: m.section ?? null, data: m.data ?? {} });
      if (m.kind === 'capture') flush();
    } else if (m.type === 'snapshot') {
      waiting.get(m.id)?.({
        items: Array.isArray(m.items) ? m.items : [],
        plan: m.plan && typeof m.plan === 'object' ? m.plan : null,
        section: m.section ?? null,
      });
      waiting.delete(m.id);
    }
  };

  // keep the reader's "still here" clock alive while the mouse is over the bar
  let lastLife = 0;
  const life = () => {
    const now = Date.now();
    if (now - lastLife < 5000) return;
    lastLife = now;
    frame.contentWindow?.postMessage({ metron: 'life' }, '*');
  };

  const stopCaptures = viewId
    ? watchCaptures((s) => {
        p.events.push({ kind: 'capture', section: current, data: { ...s } });
        flush();
      })
    : () => undefined;
  const timer = viewId ? window.setInterval(() => flush(), FLUSH_MS) : 0;
  const onHide = () => {
    if (document.visibilityState === 'hidden') flush(true);
  };
  const onPageHide = () => flush(true);

  window.addEventListener('message', onMessage);
  window.addEventListener('pointermove', life, { passive: true });
  window.addEventListener('keydown', life);
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', onPageHide);

  return {
    currentSection: () => current,
    snapshot: () =>
      new Promise((resolve) => {
        const id = Math.random().toString(36).slice(2);
        waiting.set(id, resolve);
        frame.contentWindow?.postMessage({ metron: 'snapshot', id }, '*');
        window.setTimeout(() => {
          if (waiting.has(id)) {
            waiting.delete(id);
            resolve({ items: [], plan: null, section: current });
          }
        }, 900);
      }),
    stop: () => {
      flush(true);
      window.clearInterval(timer);
      stopCaptures();
      window.removeEventListener('message', onMessage);
      window.removeEventListener('pointermove', life);
      window.removeEventListener('keydown', life);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
    },
  };
};
